import React, { useEffect, useState } from 'react';
import Tooltip from 'Components/Tooltip';
import Dropzone from 'Components/Dropzone';
import { SheerIdFinePrint } from 'Components/SheerIdFinePrint';
import { SmartLink } from 'Components/SmartLink';
import { SHEER_PAGE_TYPES, SHEER_PAGE_TITLES, getSheerIdProgram } from 'Utils/SheerIdHelpers';
import { getDefaultConfig } from 'Utils/Store';
import { submitDocUpload, requestDocUpload, completeDocUpload } from 'Api/SheerIdUpload';
import { trackEvent, TRACKING_ENUMS } from 'Utils/Analytics';
import RestartVerificationButton from '../components/RestartVerificationButton';
import ExitVerificationButton from '../components/ExitVerificationButton';
import { SheerIdHelpLink } from 'Components/FinePrint';
import { SHEER_FIRST_RESPONDER_TYPE, SHEER_MILITARY_TYPE } from 'Utils/Constants';
import CenterFrameLayout from 'Layouts/CenterFrameLayout';
import { useStore } from 'Hooks/useStore';
import './SheerIdUploadPage.css';

const { privacyPolicyPage } = getDefaultConfig();

const TooltipContent = () => (
  <div className="uploadTooltip">
    <div className="uploadTooltipText">
      Document review is handled by SheerID, a trusted partner who specializes in verifying military personnel.
    </div>
    <div className="uploadTooltipText">
      Documents are used only for verifying your military personnel status is current and will be permanently deleted
      after review is finalized. They will not be shared with Fox Nation
      <SheerIdHelpLink target="noopener noreferrer" reload text=" Get help" className="uploadTooltipLink" />
      {' +'}
      <SmartLink
        linkUrl={privacyPolicyPage}
        target="noopener noreferrer"
        reload
        text=" Privacy Policy"
        className="uploadTooltipLink"
      />
    </div>
  </div>
);

export const SheerIdUploadPage = ({ setPage, setTitle, rejectionReasons = [], layoutProps }) => {
  const routes = useStore('appConfig.routes');
  const { hasSubscription } = useStore('subscription');
  const { bango, sheerId } = useStore('integrations');
  const isBango = bango.subscriptionId;
  const { verificationId } = sheerId;

  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(rejectionReasons);
  const [clearErrors, setClearError] = useState(false);
  const [fileCount, setFileCount] = useState(0);

  useEffect(() => {
    setTitle(SHEER_PAGE_TITLES[SHEER_PAGE_TYPES.DOC_UPLOAD]);
  }, [verificationId, setTitle]);

  useEffect(() => {
    if (rejectionReasons.length && !clearErrors && rejectionReasons !== submitError) {
      setSubmitError(rejectionReasons);
    }
  }, [clearErrors, submitError, rejectionReasons]);

  useEffect(() => {
    trackEvent(TRACKING_ENUMS.EVENT_STATUS_VERIFICATION_NEEDS_MORE_INFO);
    setClearError(false);
  }, []);

  const onAddFile = () => {
    setFileCount(fileCount + 1);
    setSubmitError([]);
    setClearError(true);
  };
  const onRemoveFile = () => {
    setFileCount(fileCount - 1);
  };
  const onClearFiles = () => {
    setFileCount(0);
  };

  const onSubmit = async (values) => {
    setSubmitting(true);
    const filesToUpload = [];
    values.forEach(({ name, type, size }) =>
      filesToUpload.push({
        fileName: name,
        mimeType: type,
        fileSize: size,
      })
    );
    try {
      const response = await submitDocUpload(filesToUpload);
      await requestDocUpload(response?.documents, values);
      await completeDocUpload();
      setPage(SHEER_PAGE_TYPES.REVIEW);
    } catch (error) {
      setSubmitError([error.message]);
    }
  };

  const renderUploadInfo = () => {
    const isFirstResponder = getSheerIdProgram() === SHEER_FIRST_RESPONDER_TYPE;

    return (
      <div className="uploadInfo">
        <div className="uploadDocInfo">
          <span className="uploadInfoTitle">
            {isFirstResponder
              ? 'Upload your official document that shows:'
              : 'Upload your government-issued document that shows:'}
          </span>
          <ul className="uploadInfoList">
            <li className="uploadInfoEntry">Your full name</li>
            <li className="uploadInfoEntry">{isFirstResponder ? 'Your organization' : 'Your branch of service'}</li>
            <li className="uploadInfoEntry">
              {isFirstResponder
                ? 'Your current affiliation with that organization'
                : 'Your discharge date (if applicable)'}
            </li>
            <li className="uploadInfoEntry">
              {isFirstResponder
                ? 'Your status or license as a First Responder'
                : 'Your current affiliation with the armed services'}
            </li>
          </ul>
        </div>
        <div className="uploadDocSection">
          <span className="uploadInfoTitle">Acceptable examples include:</span>
          <ul className="uploadInfoList">
            {isFirstResponder ? (
              <>
                <li className="uploadInfoEntry">ID Card</li>
                <li className="uploadInfoEntry">Pay stub from within last 60 days</li>
                <li className="uploadInfoEntry">An official letter</li>
              </>
            ) : (
              <>
                <li className="uploadInfoEntry">
                  Any document that proves you are currently serving as active duty in the armed forces for 30 days or
                  more
                </li>
                <li className="uploadInfoEntry">Please only upload documents you are legally allowed to provide</li>
                <li className="uploadInfoEntry">
                  If your name has changed since the document was issued, upload a 2nd document showing your official
                  name change
                </li>
              </>
            )}
          </ul>
        </div>
        <div className="uploadDocSection">
          <span className="uploadInfoTitle">
            Do not upload a photo of your military ID. Under the law, SheerID is not able to process copies of Military
            ID cards.
          </span>
        </div>
      </div>
    );
  };

  return (
    <CenterFrameLayout {...layoutProps}>
      <div className="sheerIdUploadPage">
        <RestartVerificationButton setPage={setPage} isBango={isBango} />
        <ExitVerificationButton routes={routes} hasSubscription={hasSubscription} isBango={isBango} />
        <h2 className="uploadTitle">We need some more info</h2>
        {submitError.length === 0 && (
          <div className="uploadText">
            Documents will be reviewed in a few minutes by staff at SheerID, a trusted partner.{' '}
            <Tooltip offset={-35} content={<TooltipContent />}>
              <SheerIdHelpLink
                target="noopener noreferrer"
                reload
                text="How does verifying work?"
                className="uploadText highlighted"
              />
            </Tooltip>
          </div>
        )}
        {submitError.length > 0 && (
          <div className="uploadText">
            <div className="uploadErrorText">We were unable to confirm your eligibility due to:</div>
            <ul className="uploadError">
              {submitError.map((error, index) => (
                <li key={index}>{error}</li>
              ))}
            </ul>
          </div>
        )}
        <Dropzone
          onSubmit={onSubmit}
          onAddFile={onAddFile}
          onRemoveFile={onRemoveFile}
          onClearFiles={onClearFiles}
          multiple
          maxFiles={3}
          submitting={submitting}
        >
          {renderUploadInfo()}
        </Dropzone>
        <SheerIdFinePrint />
      </div>
    </CenterFrameLayout>
  );
};
